/*
 * Copyright (c) 2022 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import { TAB_KEY, TAB_KEYCODE } from '../constants/keyCodes';

// Base selectors for focusable elements (without aria-hidden filtering)
const FOCUSABLE_ELEMENT_SELECTORS = [
  'button:not([disabled]):not([hidden])',
  'input:not([disabled]):not([hidden])',
  'select:not([disabled]):not([hidden])',
  'textarea:not([disabled]):not([hidden])',
  'a[href]:not([hidden])',
  '[role="button"]:not([aria-disabled="true"]):not([hidden])',
  '[tabindex="0"]:not([hidden])'
];

// Get first interactive element for auto-focus (excludes aria-hidden elements)
const getFirstInteractiveElement = container => {
  const strictSelectors = FOCUSABLE_ELEMENT_SELECTORS.map(
    selector => `${selector}:not([aria-hidden="true"])`
  );
  return container.querySelector(strictSelectors.join(', '));
};

// Gather all focusable elements for focus trap (includes aria-hidden elements)
const gatherFocusableElements = parentElement => {
  const allSelectors = ['#rcw-conversation-container', ...FOCUSABLE_ELEMENT_SELECTORS];
  return [...parentElement.querySelectorAll(allSelectors.join(', '))];
};

const getElByClass = className => {
  return document.getElementsByClassName(className)[0];
};

const hidePickerButton = () => {
  const pickerBtn = getElByClass('rcw-picker-btn');
  if (pickerBtn) {
    pickerBtn.ariaHidden = true;
    pickerBtn.hidden = true;
  }
};

const removeExtraAriaAttributesFromMap = () => {
  setTimeout(() => {
    const googleMapParentEl = getElByClass('gm-style');
    googleMapParentEl?.setAttribute('aria-label', 'google map');
  }, 1000);
};

// Microsoft Adaptive Cards has a bug where it creates aria-labelledby values with leading spaces
// e.g., "" + " " + "id" = " id", which when split becomes ["", "id"] causing querySelector('#') errors
// This function fixes those malformed values
const fixAriaLabelledByAttribute = element => {
  const ariaLabelledBy = element.getAttribute('aria-labelledby');
  if (ariaLabelledBy !== null) {
    // Split on whitespace, filter out empty strings, rejoin with single space
    const cleanedIds = ariaLabelledBy
      .split(/\s+/)
      .filter(id => id.trim() !== '')
      .join(' ');

    if (cleanedIds === '') {
      element.removeAttribute('aria-labelledby');
    } else if (cleanedIds !== ariaLabelledBy) {
      element.setAttribute('aria-labelledby', cleanedIds);
    }
  }
};

const removeUnneededCardAttributes = card => {
  // remove focus on card wrapper
  card.removeAttribute('tabindex');

  // Fix all aria-labelledby attributes to prevent querySelector('#') errors from Microsoft Adaptive Cards bug
  const allElements = card.querySelectorAll('*');
  allElements.forEach(el => fixAriaLabelledByAttribute(el));

  // we make use of a hidden input to pass card id to jEllie. remove tabindex from this input if it exists and add hidden attributes
  const hiddenInput = card.querySelector('#card input');
  if (hiddenInput) {
    hiddenInput.hidden = true;
    hiddenInput.ariaHidden = true;
    hiddenInput.removeAttribute('tabindex');
  }
  // remove unnecessary role on submit btn wrapper divs appended by microsoft adadptive cards
  const actionSetWrappers = [...card.querySelectorAll('.ac-actionSet')];
  if (actionSetWrappers.length > 0) {
    actionSetWrappers.forEach(el => el.removeAttribute('role'));
  }
};

const setCloseButtonInfo = () => {
  const closeBtn = getElByClass('rcw-close-button');
  if (closeBtn) {
    closeBtn.ariaLabel = 'Close';
    const display = window.getComputedStyle(closeBtn).display;
    // if close btn is not visible, set the hidden attribute so it isn't deemed focusable
    if (display === 'none') {
      closeBtn.hidden = true;
      closeBtn.ariaHidden = true;
    } else {
      closeBtn.hidden = false;
      closeBtn.ariaHidden = false;
    }
  }
};

const setContainerRole = () => {
  const chatContainer = getElByClass('rcw-conversation-container');
  if (chatContainer) {
    chatContainer.setAttribute('role', 'dialog');
    chatContainer.setAttribute('tabindex', 0);
    chatContainer.ariaLabel = 'Chatbot';
  }
};

const setLogoAltText = logoLocation => {
  if (logoLocation) {
    const title = getElByClass('rcw-title');
    if (title) {
      const avatar = title.getElementsByClassName('avatar')[0];
      avatar.alt = 'CareScheduler logo';
    }
  }
};

const setNewMessageLabel = () => {
  const input = getElByClass('rcw-input');
  if (input) {
    input.ariaLabel = 'Type a message';
  }
};

const setQuickButtonA11yInfo = () => {
  const quickButtons = document.getElementsByClassName('quick-button');
  [...quickButtons].forEach(btn => (btn.ariaLabel = btn.innerText));
};

// for radio groups and checkbox groups, the containing div must have either the radiogroup (for radios) or group (for checkboxes) role
// further, the aria-required property should only be on the div with the role, and not on the individual inputs
const setRadioAndMultiselectRoles = card => {
  const radioWrappers = card.querySelectorAll('.ac-choiceSetInput-expanded');
  const checkboxWrappers = card.querySelectorAll('.ac-choiceSetInput-multiSelect');
  radioWrappers.forEach(wrap => {
    wrap.setAttribute('role', 'radiogroup');
    const inputs = wrap.querySelectorAll('input[type=radio][aria-required=true]');
    inputs.forEach(i => i.removeAttribute('aria-required'));
  });
  checkboxWrappers.forEach(wrap => {
    wrap.setAttribute('role', 'group');
    const inputs = wrap.querySelectorAll('input[type=checkbox][aria-required=true]');
    inputs.forEach(i => i.removeAttribute('aria-required'));
  });
};

const setSendButtonLabel = () => {
  const sendBtn = getElByClass('rcw-send');
  if (sendBtn) {
    sendBtn.ariaLabel = 'Send';
  }
};

const setToggleButtonInfo = isOpen => {
  const toggler = getElByClass('rcw-launcher');
  if (toggler) {
    const display = window.getComputedStyle(toggler).display;
    // if launcher btn is hidden due to close btn being visible, set appropriate a11y info so it isn't deemed focusable
    if (display === 'none' && getElByClass('rcw-close-button')) {
      toggler.hidden = true;
      toggler.ariaHidden = true;
      toggler.ariaLabel = 'Close';
      toggler.ariaExpanded = true;
    } else {
      toggler.hidden = false;
      toggler.ariaHidden = false;
      if (isOpen) {
        toggler.ariaLabel = 'Close';
        toggler.ariaExpanded = true;
      } else {
        toggler.ariaLabel = 'Open';
        toggler.ariaExpanded = false;
        toggler.focus();
      }
    }
  }
};

// ADA says that for a modal window, the focus must stay trapped in the modal. the chatbot is considered a modal.
// more info: https://hiddedevries.nl/en/blog/2017-01-29-using-javascript-to-trap-focus-in-an-element
// this is called after every time the chatbot is toggled, or an adaptive card is rendered to re-gather the focusable elements
export const trapChatbotFocus = () => {
  const chatContainer = getElByClass('rcw-widget-container');
  if (chatContainer) {
    const focusableEls = gatherFocusableElements(chatContainer);

    // set custom props on the el to be used outside of this func's context...thanks prototypical inheritance
    chatContainer.firstFocusableEl = focusableEls[0];
    chatContainer.lastFocusableEl = focusableEls[focusableEls.length - 1];

    // since focusableEls change with each card render, need to reset the handler each invocation
    chatContainer.removeEventListener('keydown', tabHandler);
    chatContainer.addEventListener('keydown', tabHandler);
  }
};

const tabHandler = e => {
  const chatContainer = e.currentTarget;
  const isTabPressed = e.key === TAB_KEY || e.keyCode === TAB_KEYCODE;
  if (!isTabPressed) {
    return;
  }
  if (e.shiftKey) {
    // shift + tab on first focus el, go to last focus el
    if (document.activeElement === chatContainer.firstFocusableEl) {
      chatContainer.lastFocusableEl.focus();
      e.preventDefault();
    }
  } else {
    // tab on last focus el, go back to first
    if (document.activeElement === chatContainer.lastFocusableEl) {
      chatContainer.firstFocusableEl.focus();
      e.preventDefault();
    }
  }
};

const resetAdaptiveCardsA11y = () => {
  const allRenderedCards = [...document.getElementsByClassName('ac-adaptiveCard')];
  if (allRenderedCards.length > 0) {
    allRenderedCards.forEach(card => {
      removeUnneededCardAttributes(card);
      setRadioAndMultiselectRoles(card);
    });
  }
};

// this function is called every time chatbot changes its isOpen state (including initial load) in Chatbot.js
export const handleBotWidgetA11y = (logoLocation, isOpen) => {
  hidePickerButton();
  setCloseButtonInfo();
  setContainerRole();
  setLogoAltText(logoLocation);
  setNewMessageLabel();
  setSendButtonLabel();
  setToggleButtonInfo(isOpen);
  // if toggled in the middle of a flow, react destroys and re-creates all the adaptive cards and quick buttons
  // so, we have to reset their a11y info when it's reopened
  if (isOpen) {
    setQuickButtonA11yInfo();
    resetAdaptiveCardsA11y();
  }
  trapChatbotFocus();
};

// this function is called every time a new adaptive card is rendered in Card.js
// skipFocus: pass true for nav-only cards (e.g. Back/Start Over) that should not steal focus
export const handleAdaptiveCardA11y = (card, skipFocus = false) => {
  removeUnneededCardAttributes(card);
  setRadioAndMultiselectRoles(card);

  // Browsers merge all <input type="radio"> with the same name into one group across the
  // entire page. If a previously submitted card has a checked radio with the same name,
  // the new card's radios are excluded from the tab order (the checked old radio "owns"
  // the group's tab stop). Setting tabindex="-1" on old card radios with conflicting
  // names removes them from the browser's group calculation, restoring correct tab behavior.
  const newCardRadioNames = new Set(
    [...card.querySelectorAll('input[type="radio"]')].map(r => r.name).filter(Boolean)
  );
  if (newCardRadioNames.size > 0) {
    document.querySelectorAll('.ac-adaptiveCard').forEach(otherCard => {
      if (otherCard === card) return;
      otherCard.querySelectorAll('input[type="radio"]').forEach(radio => {
        if (newCardRadioNames.has(radio.name)) {
          radio.tabIndex = -1;
        }
      });
    });
  }

  trapChatbotFocus();

  if (skipFocus) return;

  let focusAttempted = false;
  let renderObserver = null;

  // Microsoft Adaptive Cards library has a bug where it dynamically updates aria-labelledby
  // with leading spaces when checkbox state changes, causing querySelector('#') errors.
  // Watch for these attribute mutations and fix them immediately.
  const ariaObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'aria-labelledby') {
        fixAriaLabelledByAttribute(mutation.target);
      }
    });
  });

  ariaObserver.observe(card, {
    attributes: true,
    attributeFilter: ['aria-labelledby'],
    subtree: true
  });

  const attemptFocus = () => {
    if (focusAttempted) return;

    const firstElement = getFirstInteractiveElement(card);
    if (firstElement) {
      focusAttempted = true;
      if (renderObserver) {
        renderObserver.disconnect();
        renderObserver = null;
      }

      // Use requestAnimationFrame to ensure element is painted and ready for focus
      requestAnimationFrame(() => {
        firstElement.focus();
      });
    }
  };

  // Wait for Adaptive Cards library to fully render interactive elements
  // Use MutationObserver to detect when elements are added or attributes change (disabled -> enabled)
  renderObserver = new MutationObserver(() => {
    attemptFocus();
  });

  // Watch for child elements being added AND attribute changes (like disabled being removed)
  renderObserver.observe(card, {
    childList: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-hidden', 'hidden'],
    subtree: true
  });

  // Try to focus immediately in case elements are already there
  attemptFocus();

  // Safety timeout: stop observing after 2 seconds even if no focusable element found
  setTimeout(() => {
    if (renderObserver) {
      renderObserver.disconnect();
      renderObserver = null;
    }
  }, 2000);
};

// this function is called every time new quick buttons are rendered in Chatbot.js
export const handleQuickButtonA11y = () => {
  setQuickButtonA11yInfo();
  trapChatbotFocus();
};

// this function is called when a google map component is rendered in GoogleMap.js
export const updateGoogleMapA11yAttributes = () => {
  removeExtraAriaAttributesFromMap();
};
